import { Icon, Stack } from "@fluentui/react";
import {
  ToggleThumbIcon,
  TriangleSolidUp12Icon,
} from "@fluentui/react-icons-mdl2";
import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import Emergency from "../../../components/SVGComponents/Emergency";
import Attention from "../../../components/SVGComponents/Attention";
import Medium from "../../../components/SVGComponents/Medium";
import NoAIOutput from "../../../components/SVGComponents/NoAIOutput";
import Low from "../../../components/SVGComponents/Low";
import High from "../../../components/SVGComponents/High";
import Critical from "../../../components/SVGComponents/Critical";
import ForeignBody from "../../../components/SVGComponents/ForeignBody";
import Suspect from "../../../components/SVGComponents/Suspect";
import {
  triageStackStyles,
  triageBoxStackStyle,
  triageDetailsStackStyle,
  priorityStackStyle,
  priorityTextStyle,
  remarkTextStyle,
  verticalLine,
  annotationDivStackStyle,
  annotationStyle,
  annotationStackStyle,
  expandButtonStyle,
  annotationReportedDivStackStyle,
} from "../helpers/ConstantStyles";
import { t } from "i18next";
import { PATHOLOGY_STATUS, REPORTED_TABS_NAME, TAB_NAME, USERTYPES } from "../../../constants/Constants";
import CustomToolTipHost from "../../../components/CustomToolTipHost/CustomToolTipHost";
import NavIcons from "../../../components/navbar/nav-icons/NavIcons";
import NonMedicalEmergency from "../../../components/SVGComponents/NonMediclEmergency";
import NonFrontal from "../../../components/SVGComponents/NonFrontal";
import themeContext from "../../../context/theme/ThemeContext";
import userContext from "../../../context/users/UserContext";
import useOrientationDetector from "../../../components/OrientationDetector";
import { getEnv } from '../../../helpers/EnvHelper';
import studylistContext from "../../../context/studylist/StudylistContext";

const TriageDetails = (props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [delayHandler, setDelayHandler] = useState(null);
  const { themeName } = useContext(themeContext);
  const {showAiResults, showSavedReport} = useContext(studylistContext);
  const {roles, username, userId } = useContext(userContext)

  const predictionList = props.data.row.original.studyPredictionList;
  const vendorPredictionList = props.data.row.original.vendorPredictionList;

  // Compute how many pathology items to show based on what priority lines are visible.
  // Line 1: Emergency or Attention badge
  // Line 2: AI level badge (only rendered for non-FirmDoctor/non-FirmTechnician)
  // Portrait — Both: 3, One: 4, None: 5 | Landscape — Both: 2, One: 3, None: 4
  // If only +1 more would remain, show it directly instead of the "+1 more" button.
  const isLandscape = useOrientationDetector();
  const isPortrait = !isLandscape;
  const isFirmDoctorRestricted = roles[0].includes(USERTYPES.FIRMDOCTOR) && !props.data.row.original.showAiResult;
  const isFirmTechnicianRestricted = roles[0].includes(USERTYPES.FIRMTECHNICIAN) && !props.data.row.original.showAiResult;
  const showsAISection = !isFirmDoctorRestricted && !isFirmTechnicianRestricted;
  const row = props.data.row.original;
  const hasPathology =
    row.studyPredictionList?.length > 0 ||
    row.vendorPredictionList?.length > 0 ||
    row.studyClassificationPredictionList?.length > 0;
  const hasPriorityLine1 = row.emergency === true ||
    (!row.emergency && row.attention === true && showsAISection);
  const priorityLineCount = (hasPriorityLine1 ? 1 : 0) + (showsAISection ? 1 : 0);
  const baseVisibleCount = isPortrait
    ? (priorityLineCount >= 2 ? 3 : priorityLineCount === 1 ? 4 : 5)
    : (priorityLineCount >= 2 ? 2 : priorityLineCount === 1 ? 3 : 4);

  // adjustedBase accounts for items that wrap to multiple lines —
  // a wrapped item takes 2 line-heights, so fewer items fit.
  const [adjustedBase, setAdjustedBase] = useState(baseVisibleCount);
  const observerRef = useRef(null);

  // Reset whenever the priority section layout changes
  useEffect(() => {
    setAdjustedBase(baseVisibleCount);
  }, [baseVisibleCount]);

  const STANDARD_HEIGHT = 20; // approx single-line item height in px

  const setItemsRef = useCallback((el) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!el) return;
    const maxH = baseVisibleCount * STANDARD_HEIGHT;
    const calculate = () => {
      const children = [...el.children];
      let total = 0, count = 0;
      for (const child of children) {
        const h = child.getBoundingClientRect().height;
        if (total + h > maxH + 1) break;
        total += h;
        count++;
      }
      setAdjustedBase(Math.max(1, count || baseVisibleCount));
    };
    const observer = new ResizeObserver(calculate);
    observer.observe(el);
    observerRef.current = observer;
    calculate();
  }, [baseVisibleCount]);

  const getVisibleCount = (listLength) =>
    listLength === adjustedBase + 1 ? listLength : adjustedBase;

  const getQualityVisibleCount = (listLength) => {
    const qualityVisibleBase = 2;
    return listLength === qualityVisibleBase + 1 ? listLength : qualityVisibleBase;
  };

  const handleExpandClick = (expandValue) => {
    setIsExpanded(expandValue);
  };

  const getColorForAnnotationTags = (percent) => {
    let per = 100 - percent;
    return "hsl(" + per + ",100%,45%)";
  };

  const checkForHourGlass = (study) => {
    if (study.qualityModelMessages?.length > 0) {
      return false;
    }
    if (
      (study.aiProcessed == 2 && study.aiEmergencyMax == -1) ||
      (study.reportDoneStatus != 2 && study.aiProcessed == 2)
      //|| (study.aiProcessed != 3 && !study.expReviewDoneStatus)
    ) {
      return true;
    } else {
      return false;
    }
  };

  const showPopUp = () => {
    clearTimeout(delayHandler);
    setDelayHandler(
      setTimeout(() => {
        setIsExpanded(true);
      }, 300)
    );
  };

  const hidePopUp = () => {
    clearTimeout(delayHandler);
    setDelayHandler(
      setTimeout(() => {
        setIsExpanded(false);
      }, 300)
    );
  };

  useEffect(() => {
    clearTimeout(hidePopUp);
  }, [isExpanded]);

  useEffect(() => {
    // const scrollElement = document.querySelector('.study-list-modal-scroll');
    // const tableContainer = document.querySelector('.table-container');
    // if (scrollElement || tableContainer) {
    // const handleMouseEnter = () => {
    //   scrollElement.classList.add('scrollbar-hovered');
    //   if (tableContainer) {
    //     tableContainer.classList.add('table-hovered');
    // }
    // };
    // const handleMouseLeave = () => {
    //   scrollElement.classList.remove('scrollbar-hovered');
    //   if (tableContainer) {
    //     tableContainer.classList.remove('table-hovered');
    // }
    // };
    // const handleMouseMove = (event) => {
    //     const scrollbarWidth = scrollElement.offsetWidth - scrollElement.clientWidth;
    //     const scrollbarRect = scrollElement.getBoundingClientRect();
    //     const scrollbarStartX = scrollbarRect.right - scrollbarWidth;
    //     const hoverStartX = scrollbarStartX - 5;
    //     const hoverEndX = scrollbarRect.right + 20; // 20px on the right side
    //     if (event.clientX >= hoverStartX && event.clientX <= hoverEndX) {
    //         scrollElement.classList.add('scrollbar-hovered');
    //     } else {
    //         scrollElement.classList.remove('scrollbar-hovered');
    //     }
    // };
    //   scrollElement.addEventListener('mouseenter', handleMouseEnter);
    //   scrollElement.addEventListener('mouseleave', handleMouseLeave);
    //   scrollElement.addEventListener('mousemove', handleMouseMove);
    //   return () => {
    //     scrollElement.removeEventListener('mouseenter', handleMouseEnter);
    //     scrollElement.removeEventListener('mouseleave', handleMouseLeave);
    //     scrollElement.removeEventListener('mousemove', handleMouseMove);
    // };
    //  }
  }, [document.querySelector(".study-list-modal-scroll")]);

  useEffect(() => {
    if (REPORTED_TABS_NAME.includes(props.tabId)) {
      let lists = [...props.data.row.original.studyPredictionList];
      function customSort(a, b) {
        if (a.aiPredicted && a.selected) {
          return -1;
        } else if (b.aiPredicted && b.selected) {
          return 1;
        } else if (!a.aiPredicted && !b.aiPredicted) {
          return 0;
        } else if (!a.aiPredicted) {
          return -1;
        } else if (!b.aiPredicted) {
          return 1;
        } else if (a.selected && !b.selected) {
          return -1;
        } else if (!a.selected && b.selected) {
          return 1;
        }
      }
      lists.sort(customSort);
      props.data.row.original.studyPredictionList = lists;
    }
  }, []);

  return (
    <>
      {
        <Stack
          horizontal
          style={triageStackStyles}
          data-tut={"tour-triage-details"}
        >
          <Stack
            horizontal
            className={`studylist-triage-container ${predictionList?.length > 0 || vendorPredictionList?.length > 0 ? "studylist-triage-flex" : ""}`}
            style={{ ...triageBoxStackStyle, position: "relative" }}
          >
            <Stack
              style={
                row.qualityModelEnabled &&
                row.qualityModelMessages?.length > 0 &&
                !hasPathology
                  ? { ...triageDetailsStackStyle, justifyContent: "flex-start" }
                  : triageDetailsStackStyle
              }
            >
              {/* Case 1: Quality ON + No Pathology -> Show full quality block on top */}
              {row.qualityModelEnabled &&
                row.qualityModelMessages?.length > 0 &&
                !hasPathology && (
                  <>
                    <Stack style={{ ...priorityStackStyle, marginTop: "4px" }}>
                      <span
                        className="studylist-triage-info-text"
                        style={priorityTextStyle}
                      >
                        <Attention /> &nbsp; Poor Quality Image
                      </span>
                    </Stack>

                    <div
                      style={annotationDivStackStyle}
                      className="modal-scroll"
                    >
                      <div
                        ref={setItemsRef}
                        style={{
                          maxHeight: `${baseVisibleCount * STANDARD_HEIGHT}px`,
                          overflow: "hidden",
                        }}
                      >
                        {row.qualityModelMessages
                          .slice()
                          .sort((a, b) => a.localeCompare(b))
                          .map((message, index) => {
                            if (
                              index <
                              getQualityVisibleCount(
                                row.qualityModelMessages.length,
                              )
                            ) {
                              const className = message.split("-")[0];

                              return (
                                <Stack
                                  key={index}
                                  horizontal
                                  style={{
                                    ...annotationStackStyle,
                                    width: "100%",
                                    justifyContent: "flex-start",
                                  }}
                                >
                                  <CustomToolTipHost
                                    content={
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "2px",
                                        }}
                                      >
                                        {row.qualityModelMessages.map(
                                          (msg, i) => (
                                            <span
                                              key={i}
                                              className="studylist-triage-finding-text"
                                              style={{
                                                display: "block",
                                                width: "100%",
                                                textAlign: "left",
                                              }}
                                            >
                                              <span className="finding-text-triage-column">
                                                {msg}
                                              </span>
                                            </span>
                                          ),
                                        )}
                                      </div>
                                    }
                                  >
                                    <span
                                      className="studylist-triage-finding-text cursor-pointer"
                                      style={{
                                        justifyContent: "flex-start",
                                        textAlign: "left",
                                        width: "100%",
                                        paddingLeft: "33px",
                                      }}
                                    >
                                      <span className="finding-text-triage-column">
                                        {className}
                                      </span>
                                    </span>
                                  </CustomToolTipHost>
                                </Stack>
                              );
                            }

                            return null;
                          })}
                      </div>

                      {/* {row.qualityModelMessages.length >
                        getQualityVisibleCount(row.qualityModelMessages.length) && (
                        <CustomToolTipHost
                          content={
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "2px",
                              }}
                            >
                              {row.qualityModelMessages.map((msg, i) => (
                                <span
                                  key={i}
                                  className="studylist-triage-finding-text"
                                  style={{ justifyContent: "center" }}
                                >
                                  <span className="finding-text-triage-column">
                                    {msg}
                                  </span>
                                </span>
                              ))}
                            </div>
                          }
                        >
                          <div
                            className="cursor-pointer"
                            style={expandButtonStyle}
                          >
                            +
                            {row.qualityModelMessages.length -
                              getQualityVisibleCount(
                                row.qualityModelMessages.length,
                              )}{" "}
                            more
                          </div>
                        </CustomToolTipHost>
                      )} */}
                      {row.qualityModelMessages.length >
                        getQualityVisibleCount(
                          row.qualityModelMessages.length,
                        ) && (
                        <div
                          className="cursor-pointer"
                          style={expandButtonStyle}
                          onClick={() => handleExpandClick(true)}
                          onMouseEnter={(e) => {
                            e.stopPropagation();
                            showPopUp();
                          }}
                          onMouseLeave={(e) => {
                            e.stopPropagation();
                            hidePopUp();
                          }}
                        >
                          +
                          {row.qualityModelMessages.length -
                            getQualityVisibleCount(
                              row.qualityModelMessages.length,
                            )}{" "}
                          more
                        </div>
                      )}

                      {isExpanded && (
                        <div style={{ position: "relative" }}>
                          <PopUpAIPathologyDetails
                            data={props.data}
                            onClickOutside={() => setIsExpanded(false)}
                            hidePopUp={hidePopUp}
                            showPopUp={showPopUp}
                            show={isExpanded}
                            setIsExpanded={setIsExpanded}
                            tabId={props.tabId}
                            visibleCount={getQualityVisibleCount(
                              row.qualityModelMessages.length,
                            )}
                            qualityMessages={row.qualityModelMessages}
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
              <Stack
                style={priorityStackStyle}
                className={`${themeName === "light" && (predictionList?.length > 0 || vendorPredictionList?.length > 0) && ((!roles[0].includes(USERTYPES.FIRMDOCTOR) && !roles[0].includes(USERTYPES.FIRMTECHNICIAN)) || props.data.row.original.showAiResult) ? "studylist-triage-details-header" : ""}`}
              >
                <span
                  className="studylist-triage-info-text"
                  style={priorityTextStyle}
                >
                  {props.data.row.original.emergency === true &&
                  !props.data.row.original.nonMedicalEmergency === true ? (
                    <>
                      <Emergency /> &nbsp;{t("study-list.badges.emergency")}
                    </>
                  ) : (
                    <></>
                  )}
                  {props.data.row.original.emergency === true &&
                  props.data.row.original.nonMedicalEmergency === true ? (
                    <>
                      <div>
                        <NonMedicalEmergency />
                      </div>{" "}
                      &nbsp;{" "}
                      <CustomToolTipHost
                        content={t("study-list.badges.non-medical-emergency")}
                      >
                        {t("study-list.badges.non-med-emergency")}
                      </CustomToolTipHost>
                    </>
                  ) : (
                    <></>
                  )}

                  {!props.data.row.original.emergency &&
                  props.data.row.original.attention === true &&
                  ((!roles[0].includes(USERTYPES.FIRMDOCTOR) &&
                    !roles[0].includes(USERTYPES.FIRMTECHNICIAN)) ||
                    props.data.row.original.showAiResult ) ? (
                    <>
                      <Attention /> &nbsp;{t("study-list.badges.attention")}
                    </>
                  ) : (
                    <></>
                  )}
                  {/* Case 2: Quality ON + Has Pathology -> Show only icon next to AI badge (RIGHT) */}
                  {row.qualityModelEnabled &&
                    row.qualityModelMessages?.length > 0 &&
                    hasPathology && (
                      <CustomToolTipHost
                        // directionalHint={5}
                        content={
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "600",
                                marginBottom: "4px",
                                display: "block",
                              }}
                            >
                              Poor Quality Image
                            </span>
                            {row.qualityModelMessages
                              .slice()
                              .sort((a, b) => a.localeCompare(b))
                              .map((message, index) => (
                                <span
                                  key={index}
                                  style={{ fontSize: "12px", opacity: "0.9" }}
                                >
                                  {message}
                                </span>
                              ))}
                          </div>
                        }
                      >
                        <span
                          style={{
                            position: "absolute",
                            right: "6px",
                            top: "4px",
                            cursor: "pointer",
                            display: "inline-flex",
                          }}
                        >
                          <Attention />
                        </span>
                      </CustomToolTipHost>
                    )}
                </span>
                {((!roles[0].includes(USERTYPES.FIRMDOCTOR) &&
                  !roles[0].includes(USERTYPES.FIRMTECHNICIAN)) ||
                  props.data.row.original.showAiResult) && (
                  <span
                    className="studylist-triage-info-text"
                    style={priorityTextStyle}
                  >
                    {(props.data.row.original.aiEmergencyMax === -1 &&
                      props.data.row.original.aiProcessed == 0) ||
                    (props.data.row.original.reportDoneStatus != 2 &&
                      props.data.row.original.aiProcessed == 0) ||
                    props.data.row.original.aiProcessed == 3 ? (
                      <>
                        <div>
                          <NoAIOutput />
                        </div>{" "}
                        &nbsp;
                        {t("study-list.filters.no-ai-output")}
                      </>
                    ) : (
                      <></>
                    )}
                    {checkForHourGlass(props.data.row.original) ? (
                      <>
                        <Icon iconName="HourGlass" /> &nbsp;{" "}
                        {t("study-list.badges.in-progress")}...
                      </>
                    ) : (
                      <>
                        {props.data.row.original.aiEmergencyMax === -3 ? (
                          <>
                            <NonFrontal />
                            &nbsp; {t("study-list.filters.non-frontal")}
                          </>
                        ) : (
                          <></>
                        )}
                        {props.data.row.original.aiEmergencyMax === 1 ||
                        props.data.row.original.aiEmergencyMax === 0 ? (
                          <>
                            <Low />
                            &nbsp; {t("study-list.filters.low")}
                          </>
                        ) : (
                          <></>
                        )}
                        {props.data.row.original.aiEmergencyMax === 2 ? (
                          <>
                            <Medium />
                            &nbsp; {t("study-list.filters.medium")}
                          </>
                        ) : (
                          <></>
                        )}
                        {props.data.row.original.aiEmergencyMax === 3 ? (
                          <>
                            <High />
                            &nbsp; {t("study-list.filters.high")}
                          </>
                        ) : (
                          <></>
                        )}
                        {props.data.row.original.aiEmergencyMax === 4 ? (
                          <>
                            <Critical /> &nbsp;{" "}
                            {t("study-list.filters.critical")}
                          </>
                        ) : (
                          <></>
                        )}
                        {props.data.row.original.aiEmergencyMax === 7 ? (
                          <>
                            <Suspect /> &nbsp;{t("study-list.filters.suspect")}
                          </>
                        ) : (
                          <></>
                        )}
                      </>
                    )}
                    {props.data.row.original.aiEmergencyMax === 6 ? (
                      <>
                        <ForeignBody /> &nbsp;{t("study-list.filters.fo-only")}
                      </>
                    ) : (
                      <></>
                    )}
                  </span>
                )}
              </Stack>
              {((!roles[0].includes(USERTYPES.FIRMDOCTOR) &&
                !roles[0].includes(USERTYPES.FIRMTECHNICIAN)) ||
                props.data.row.original.showAiResult) && (
                <>
                  {props.data.row.original.xrayVersion === true &&
                    props.data.row.original.studyClassificationPredictionList
                      .length > 0 && (
                      <div
                        style={annotationDivStackStyle}
                        className="modal-scroll"
                      >
                        <div
                          ref={setItemsRef}
                          style={{
                            maxHeight: `${baseVisibleCount * STANDARD_HEIGHT}px`,
                            overflow: "hidden",
                          }}
                        >
                          {props.data.row.original.studyClassificationPredictionList.map(
                            (prediction, index) => {
                              if (
                                index <
                                getVisibleCount(
                                  props.data.row.original
                                    .studyClassificationPredictionList.length,
                                )
                              ) {
                                return (
                                  <Stack
                                    key={index}
                                    horizontal
                                    style={annotationStackStyle}
                                  >
                                    <span
                                      className="studylist-triage-finding-text"
                                      style={{ justifyContent: "center" }}
                                    >
                                      <span className="finding-text-triage-column">
                                        {prediction.classificationName}
                                      </span>
                                    </span>
                                  </Stack>
                                );
                              }
                            },
                          )}
                        </div>
                        {props.data.row.original
                          .studyClassificationPredictionList.length >
                          getVisibleCount(
                            props.data.row.original
                              .studyClassificationPredictionList.length,
                          ) && (
                          <div
                            className="cursor-pointer"
                            style={expandButtonStyle}
                            onClick={() => handleExpandClick(true)}
                            onMouseEnter={(e) => {
                              e.stopPropagation();
                              showPopUp();
                            }}
                            onMouseLeave={(e) => {
                              e.stopPropagation();
                              hidePopUp();
                            }}
                          >
                            +
                            {props.data.row.original
                              .studyClassificationPredictionList.length -
                              getVisibleCount(
                                props.data.row.original
                                  .studyClassificationPredictionList.length,
                              )}{" "}
                            more
                          </div>
                        )}
                        {isExpanded && (
                          <div style={{ position: "relative" }}>
                            <PopUpAIPathologyDetails
                              data={props.data}
                              onClickOutside={() => setIsExpanded(false)}
                              hidePopUp={hidePopUp}
                              showPopUp={showPopUp}
                              show={isExpanded}
                              setIsExpanded={setIsExpanded}
                              tabId={props.tabId}
                              visibleCount={getVisibleCount(
                                props.data.row.original
                                  .studyClassificationPredictionList.length,
                              )}
                            />
                          </div>
                        )}
                      </div>
                    )}

                  {props.data.row.original.xrayVersion === false &&
                    !REPORTED_TABS_NAME.includes(props.tabId) && (
                      <>
                        {/* Case 1: Show studyPredictionList if it has items */}
                        {props.data.row.original.studyPredictionList.length >
                          0 && (
                          <div
                            style={annotationDivStackStyle}
                            className="modal-scroll"
                          >
                            <div
                              ref={setItemsRef}
                              style={{
                                maxHeight: `${baseVisibleCount * STANDARD_HEIGHT}px`,
                                overflow: "hidden",
                              }}
                            >
                              {props.data.row.original.studyPredictionList.map(
                                (prediction, index) => {
                                  if (
                                    index <
                                    getVisibleCount(
                                      props.data.row.original
                                        .studyPredictionList.length,
                                    )
                                  ) {
                                    return (
                                      <Stack
                                        key={index}
                                        horizontal
                                        style={annotationStackStyle}
                                      >
                                        <span
                                          className="studylist-triage-finding-text"
                                          style={{
                                            justifyContent: `${props.data.row.original?.showProbability ? "" : "center"}`,
                                          }}
                                        >
                                          <span className="finding-text-triage-column">
                                            {prediction.pathology.name}
                                          </span>
                                          {props.data.row.original
                                            .showProbability &&
                                            getEnv(
                                              "REACT_APP_PROTECTED_MODE",
                                            ) == "false" && (
                                              <span>
                                                {"-"}&nbsp;&nbsp;&nbsp;
                                                {`${prediction.probabilityDisplay}%`}
                                              </span>
                                            )}
                                        </span>
                                      </Stack>
                                    );
                                  }
                                },
                              )}
                            </div>
                            {props.data.row.original.studyPredictionList
                              .length >
                              getVisibleCount(
                                props.data.row.original.studyPredictionList
                                  .length,
                              ) && (
                              <div
                                className="cursor-pointer"
                                style={expandButtonStyle}
                                onClick={() => handleExpandClick(true)}
                                onMouseEnter={(e) => {
                                  e.stopPropagation();
                                  showPopUp();
                                }}
                                onMouseLeave={(e) => {
                                  e.stopPropagation();
                                  hidePopUp();
                                }}
                              >
                                +
                                {props.data.row.original.studyPredictionList
                                  .length -
                                  getVisibleCount(
                                    props.data.row.original.studyPredictionList
                                      .length,
                                  )}{" "}
                                more
                              </div>
                            )}
                            {isExpanded && (
                              <div style={{ position: "relative" }}>
                                <PopUpAIPathologyDetails
                                  data={props.data}
                                  onClickOutside={() => setIsExpanded(false)}
                                  hidePopUp={hidePopUp}
                                  showPopUp={showPopUp}
                                  show={isExpanded}
                                  setIsExpanded={setIsExpanded}
                                  tabId={props.tabId}
                                  visibleCount={getVisibleCount(
                                    props.data.row.original.studyPredictionList
                                      .length,
                                  )}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}

                  {props.data.row.original.xrayVersion === false && (
                    <>
                      {/* Case 2: Show vendorPredictionList if studyPredictionList empty */}
                      {props.data.row.original.studyPredictionList.length ===
                        0 &&
                        props.data.row.original.vendorPredictionList &&
                        props.data.row.original.vendorPredictionList.length >
                          0 && (
                          <div
                            style={annotationDivStackStyle}
                            className="modal-scroll"
                          >
                            <div
                              ref={setItemsRef}
                              style={{
                                maxHeight: `${baseVisibleCount * STANDARD_HEIGHT}px`,
                                overflow: "hidden",
                              }}
                            >
                              {props.data.row.original.vendorPredictionList.map(
                                (prediction, index) => {
                                  if (
                                    index <
                                    getVisibleCount(
                                      props.data.row.original
                                        .vendorPredictionList.length,
                                    )
                                  ) {
                                    return (
                                      <Stack
                                        key={index}
                                        horizontal
                                        style={annotationStackStyle}
                                      >
                                        <div
                                          className="studylist-triage-finding-text"
                                          style={{
                                            display: "flex",
                                            justifyContent: `${props.data.row.original?.showProbability ? "flex-start" : "center"}`,
                                            alignItems: "center",
                                            gap: "6px",
                                            lineHeight: "1.2",
                                          }}
                                        >
                                          <span
                                            className="finding-text-triage-column"
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            {capitalize(prediction.pathology)}
                                          </span>
                                          {props.data.row.original
                                            .showProbability &&
                                            getEnv(
                                              "REACT_APP_PROTECTED_MODE",
                                            ) == "false" && (
                                              <>
                                                <span>-</span>
                                                <span>
                                                  {capitalize(prediction.value)}
                                                </span>
                                              </>
                                            )}
                                        </div>
                                      </Stack>
                                    );
                                  }
                                },
                              )}
                            </div>
                            {props.data.row.original.vendorPredictionList
                              .length >
                              getVisibleCount(
                                props.data.row.original.vendorPredictionList
                                  .length,
                              ) && (
                              <div
                                className="cursor-pointer"
                                style={expandButtonStyle}
                                onClick={() => handleExpandClick(true)}
                                onMouseEnter={(e) => {
                                  e.stopPropagation();
                                  showPopUp();
                                }}
                                onMouseLeave={(e) => {
                                  e.stopPropagation();
                                  hidePopUp();
                                }}
                              >
                                +
                                {props.data.row.original.vendorPredictionList
                                  .length -
                                  getVisibleCount(
                                    props.data.row.original.vendorPredictionList
                                      .length,
                                  )}{" "}
                                more
                              </div>
                            )}
                            {isExpanded && (
                              <div style={{ position: "relative" }}>
                                <PopUpAIPathologyDetails
                                  data={props.data}
                                  onClickOutside={() => setIsExpanded(false)}
                                  hidePopUp={hidePopUp}
                                  showPopUp={showPopUp}
                                  show={isExpanded}
                                  setIsExpanded={setIsExpanded}
                                  tabId={props.tabId}
                                  visibleCount={getVisibleCount(
                                    props.data.row.original.vendorPredictionList
                                      .length,
                                  )}
                                />
                              </div>
                            )}
                          </div>
                        )}
                    </>
                  )}

                  {props.data.row.original.xrayVersion === false &&
                    REPORTED_TABS_NAME.includes(props.tabId) &&
                    props.data.row.original.studyPredictionList.length > 0 && (
                      <div
                        style={annotationReportedDivStackStyle}
                        className="modal-scroll"
                      >
                        <div
                          ref={setItemsRef}
                          style={{
                            maxHeight: `${baseVisibleCount * STANDARD_HEIGHT}px`,
                            overflow: "hidden",
                          }}
                        >
                          {props.data.row.original.studyPredictionList.map(
                            (prediction, index) => {
                              if (
                                index <
                                getVisibleCount(
                                  props.data.row.original.studyPredictionList
                                    .length,
                                )
                              ) {
                                return (
                                  <React.Fragment key={index}>
                                    <span
                                      className="studylist-triage-finding-text"
                                      style={{ padding: "0px" }}
                                    >
                                      <PathologyDetails
                                        prediction={prediction}
                                      />
                                    </span>
                                  </React.Fragment>
                                );
                              }
                            },
                          )}
                        </div>
                        {isExpanded && (
                          <div style={{ position: "relative" }}>
                            <PopUpAIPathologyDetails
                              data={props.data}
                              onClickOutside={() => setIsExpanded(false)}
                              hidePopUp={hidePopUp}
                              showPopUp={showPopUp}
                              show={isExpanded}
                              setIsExpanded={setIsExpanded}
                              tabId={props.tabId}
                              visibleCount={getVisibleCount(
                                props.data.row.original.studyPredictionList
                                  .length,
                              )}
                            />
                          </div>
                        )}
                        </div>
                      )}
                  </>
                )}
            </Stack>
          </Stack>
        </Stack>
      }
    </>
  );
};

export default TriageDetails;

const capitalize = (str) => {
  if (!str || typeof str !== "string") return ""; // handles null, undefined, numbers, empty
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};


const PathologyDetails = (props) => {
  const prediction = props.prediction;

  const getTypeOfAcceptance = (aiPredicted, isSelected) => {
    switch (true) {
      case aiPredicted && isSelected:
        return PATHOLOGY_STATUS.VERIFIED;

      case aiPredicted && !isSelected:
        return PATHOLOGY_STATUS.REJECTED;

      case !aiPredicted && isSelected:
        return PATHOLOGY_STATUS.ADDED;

      default:
        return PATHOLOGY_STATUS.ADDED;
    }
  };

  const customTriageColumnTooltip = {
    root: "triage-details-pathology-name",
  };

  const checkForPercentagePresent = (aiPredicted, isSelected) => {
    if (getEnv("REACT_APP_PROTECTED_MODE") == "false") {
      if (!aiPredicted) {
        return false;
      }
      return true;
    }
    return false;
  };

  return (
    <>
      <div className="studylist-triage-reported-container">
        <CustomToolTipHost
          styles={customTriageColumnTooltip}
          id="path-percent"
          content={
            checkForPercentagePresent(
              prediction.aiPredicted,
              prediction.selected
            )
              ? `${prediction.pathology.name} - ${prediction.probabilityOriginalPercentage}%`
              : prediction.pathology.name
          }
        >
          <span>{prediction.pathology.name}</span>
        </CustomToolTipHost>
        <span style={{ display: "flex" }}>
          <PathologyIcon
            type={getTypeOfAcceptance(
              prediction.aiPredicted,
              prediction.selected
            )}
          />
        </span>
      </div>
    </>
  );
};

const PopUpAIPathologyDetails = (props) => {
  const ref = useRef(null);
  const { onClickOutside } = props;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClickOutside && onClickOutside();
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [onClickOutside]);

  if (!props.show) return null;

  return (
    <>
      <div
        ref={ref}
        onMouseEnter={props.showPopUp}
        onMouseLeave={props.hidePopUp}
        style={{
          padding: !REPORTED_TABS_NAME.includes(props.tabId) ? "10px 15px" : "10px",
        }}
        className="study-list-triage-details-popup study-list-triage-arrow-up"
      >
        {props.data.row.original.xrayVersion === false &&
          props.data.row.original.studyPredictionList.length > 0 &&
          props.data.row.original.studyPredictionList.map(
            (prediction, index) => {
              if (index >= props.visibleCount) {
                return !REPORTED_TABS_NAME.includes(props.tabId) ? (
                  <Stack key={index} horizontal style={annotationStackStyle}>
                    <span className="studylist-triage-finding-text" style={{ justifyContent: `${props.data.row.original?.showProbability? '':'center'}` }}>
                      <span className="finding-text-triage-column">
                        {prediction.pathology.name}
                      </span>
                      {props.data.row.original.showProbability && getEnv("REACT_APP_PROTECTED_MODE") == "false" && (
                        <span>
                          {"-"}&nbsp;&nbsp;&nbsp;
                          {`${prediction.probabilityDisplay}%`}
                        </span>
                      )}
                    </span>
                  </Stack>
                ) : (
                  <React.Fragment key={index}>
                    <span
                      className="studylist-triage-finding-text"
                      style={{ padding: "0px" }}
                    >
                      <PathologyDetails prediction={prediction} />
                    </span>
                  </React.Fragment>
                );
              }
            }
          )}

        {props.data.row.original.studyPredictionList.length === 0 &&
        props.data.row.original.vendorPredictionList &&
        props.data.row.original.vendorPredictionList.length > 0 &&
        props.data.row.original.vendorPredictionList.map((prediction, index) => {
          if (index >= props.visibleCount) {
            return (
              <Stack key={index} horizontal style={annotationStackStyle}>
                <div
                  className="studylist-triage-finding-text"
                  style={{
                    display: "flex",
                    justifyContent: `${
                      props.data.row.original?.showProbability
                        ? "flex-start"
                        : "center"
                    }`,
                    alignItems: "center",
                  }}
                >
                  <span className="finding-text-triage-column">
                    {capitalize(prediction.pathology)}
                  </span>
                  {props.data.row.original.showProbability &&
                    getEnv("REACT_APP_PROTECTED_MODE") == "false" && (
                      <>
                        <span style={{ margin: "0 8px" }}>-</span>
                        <span>{capitalize(prediction.value)}</span>
                      </>
                    )}
                </div>
              </Stack>
            );
          }
        })}

        {props.data.row.original.xrayVersion === true &&
          props.data.row.original.studyClassificationPredictionList.length >
            0 &&
          props.data.row.original.studyClassificationPredictionList.map(
            (prediction, index) => {
              if (index >= props.visibleCount) {
                return  (
                  <Stack key={index} horizontal style={annotationStackStyle}>
                    <span className="studylist-triage-finding-text">
                      <span className="finding-text-triage-column">
                        {prediction.classificationName}
                      </span>
                      {/* {getEnv("REACT_APP_PROTECTED_MODE") == "false" && (
                        <span>
                          {"-"}&nbsp;&nbsp;&nbsp;
                          {`${prediction.probabilityDisplay}%`}
                        </span>
                      )} */}
                    </span>
                  </Stack>
                
                );
              }
            }
          )}
      </div>
    </>
  );
};

const PathologyIcon = ({ type }) => {
  switch (true) {
    case type === "verified":
      return (
        <div className="triage-container">
          <NavIcons iconName="verified" />
          <span className="triage-verified-text">
            {t("study-list.badges.verified")}
          </span>
        </div>
      );

    case type === "added":
      return (
        <div className="triage-container">
          <NavIcons iconName="added" />
          <span className="triage-added-text">
            {t("study-list.badges.added")}
          </span>
        </div>
      );

    case type === "rejected":
      return (
        <div className="triage-container">
          <NavIcons iconName="rejected" />
          <span className="triage-rejected-text">
            {t("study-list.badges.rejected")}
          </span>
        </div>
      );
    default:
      return <></>;
  }
};
